import React from 'react';
import { Box, Button, Stack } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { Country } from '@trnsact/trnsact-shared-types';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';

import { RulesSection } from './RulesSection';
import { CommonSection } from './CommonSection';
import { ChargesSection } from './ChargesSection';
import { TaxesSection } from './TaxesSection';
import { useChargesConfiguration } from '../../api';
import { dealSheetConfigurationMapper } from '../../mappers';
import { DealSheetFormValues, salesCenterFormSchema } from './types';

export const DealSheetRuleForm = ({ onCancel }: { onCancel: VoidFunction }) => {
  const { isConfigurationCreating, handleCreateConfiguration } = useChargesConfiguration();

  const form = useForm<DealSheetFormValues>({
    mode: 'all',
    resolver: zodResolver(salesCenterFormSchema),
    defaultValues: {
      name: '',
      description: '',

      taxes: [],
      charges: [],

      equipment: { type: [] },
      customer: { type: [], paymentType: [] },
      address: { zip: [], state: [], county: [], country: Country.Us, city: [], postalCode: [], province: [] },
    },
  });

  const handleSubmitDealRules = async (formData: DealSheetFormValues) => {
    const dto = dealSheetConfigurationMapper.processSalesCenterFormToAPIDto(formData);

    await handleCreateConfiguration(dto, api => ({
      refetchQueries: [api.getConfigs],
      awaitRefetchQueries: true,
    }));

    form.reset();

    onCancel();
  };

  return (
    <FormProvider {...form}>
      <Stack className="salesCenterCard" gap="1rem" height="100%" padding="0 0.5rem">
        <CommonSection />

        <Box height="100%">
          <TabsContainer
            tabs={[
              { label: 'Conditions', value: 'common', component: <RulesSection /> },
              { label: 'Charges & Fees', value: 'charges', component: <ChargesSection /> },
              { label: 'Taxes', value: 'tax', component: <TaxesSection /> },
            ]}
          />
        </Box>

        <Stack direction="row" gap="0.5rem">
          <Button
            variant="contained"
            onClick={form.handleSubmit(handleSubmitDealRules)}
            loading={form.formState.isSubmitting || isConfigurationCreating}
          >
            Save Rule
          </Button>

          <Button color="error" onClick={() => form.reset()} disabled={isConfigurationCreating}>
            Reset
          </Button>

          <Button onClick={onCancel} disabled={isConfigurationCreating}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};
